import React, { PropsWithChildren } from 'react';
import { Box, Typography } from '@mui/material';
import { MoSpacing } from '../MoSpacing';

export const SessionNotesQuestions = {
  clinicalTopics: 'Select the core topics you addressed.',
  clinicalSkills: 'Select the skills you practiced or strengthened.',
  clinicalPracticeApproaches:
    'Select any therapeutic interventions, modalities, or frameworks you explored.',
  professionalDevelopmentTools:
    'Select any professional development tools or strategies you offered.',
  additionalFeedback: 'Document any follow-up actions for your next session.',
};

const NotesItem = ({ title, children }: PropsWithChildren<{ title: string }>) => (
  <>
    <Typography fontWeight={700} color="grey.700" sx={{ marginBottom: 1 }}>
      {title}
    </Typography>
    {children}
  </>
);

const NotesList = ({ items }: { items: string[] }) => (
  <ul style={{ margin: 0 }}>
    {items.map((item) => (
      <li style={{ marginBottom: 4 }} key={item}>
        {item}
      </li>
    ))}
  </ul>
);

const NotesOther = ({ other }: { other: string }) => (
  <li style={{ margin: '0 0 0 20px' }}>Other: {other}</li>
);

export type LegacySessionNote = { id: number; question: string; answer: string };

export type SessionNotesInfoData = {
  id: string;
  clinicalTopics: { id: number; name: string }[];
  clinicalTopicsOther?: string | null;
  clinicalSkills: { id: number; name: string }[];
  clinicalSkillsOther?: string | null;
  clinicalPracticeApproaches: { id: number; name: string }[];
  clinicalPracticeApproachesOther?: string | null;
  clinicalPracticeApproachesNotApplicable?: boolean | null;
  professionalDevelopmentTools: { id: number; name: string }[];
  professionalDevelopmentToolsOther?: string | null;
  professionalDevelopmentToolsNotApplicable?: boolean | null;
  additionalFeedback?: string | null;
};

function sessionNotesInfoHasContent(info: SessionNotesInfoData): boolean {
  return (
    info.clinicalTopics.length > 0 ||
    !!info.clinicalTopicsOther ||
    info.clinicalSkills.length > 0 ||
    !!info.clinicalSkillsOther ||
    info.clinicalPracticeApproaches.length > 0 ||
    !!info.clinicalPracticeApproachesOther ||
    info.clinicalPracticeApproachesNotApplicable != null ||
    info.professionalDevelopmentTools.length > 0 ||
    !!info.professionalDevelopmentToolsOther ||
    info.professionalDevelopmentToolsNotApplicable != null
  );
}

export function hasSessionNotesContent(
  legacyNotes: LegacySessionNote[] | null | undefined,
  sessionNotesInfo: SessionNotesInfoData | null | undefined,
): boolean {
  if (legacyNotes && legacyNotes.length > 0) return true;
  return !!sessionNotesInfo && sessionNotesInfoHasContent(sessionNotesInfo);
}

export default function SessionNotesContent({
  legacyNotes,
  sessionNotesInfo,
}: {
  legacyNotes: LegacySessionNote[] | null;
  sessionNotesInfo: SessionNotesInfoData | null | undefined;
}) {
  if (legacyNotes && legacyNotes.length > 0) {
    return (
      <>
        {legacyNotes.map(({ id, question, answer }) => (
          <MoSpacing y={2} key={id}>
            <Typography variant="body1">
              <strong>{question}</strong>
            </Typography>
            <Box mb={3} whiteSpace="pre-line">
              <Typography variant="body1">{answer}</Typography>
            </Box>
          </MoSpacing>
        ))}
      </>
    );
  }

  if (sessionNotesInfo) {
    const {
      clinicalTopics,
      clinicalTopicsOther,
      clinicalSkills,
      clinicalSkillsOther,
      clinicalPracticeApproaches,
      clinicalPracticeApproachesOther,
      clinicalPracticeApproachesNotApplicable,
      professionalDevelopmentTools,
      professionalDevelopmentToolsOther,
      professionalDevelopmentToolsNotApplicable,
      additionalFeedback,
    } = sessionNotesInfo;

    const hasSkillsAndTopics = sessionNotesInfoHasContent(sessionNotesInfo);

    return (
      <>
        <MoSpacing y={4}>
          {hasSkillsAndTopics && (
            <>
              <NotesItem title={SessionNotesQuestions.clinicalTopics}>
                <NotesList items={clinicalTopics.map(({ name }) => name)} />
                {clinicalTopicsOther && <NotesOther other={clinicalTopicsOther} />}
              </NotesItem>

              <NotesItem title={SessionNotesQuestions.clinicalSkills}>
                <NotesList items={clinicalSkills.map(({ name }) => name)} />
                {clinicalSkillsOther && <NotesOther other={clinicalSkillsOther} />}
              </NotesItem>

              <NotesItem title={SessionNotesQuestions.clinicalPracticeApproaches}>
                {clinicalPracticeApproachesNotApplicable ? (
                  'Not applicable to this session'
                ) : (
                  <NotesList items={clinicalPracticeApproaches.map(({ name }) => name)} />
                )}
                {clinicalPracticeApproachesOther && (
                  <NotesOther other={clinicalPracticeApproachesOther} />
                )}
              </NotesItem>

              <NotesItem title={SessionNotesQuestions.professionalDevelopmentTools}>
                {professionalDevelopmentToolsNotApplicable ? (
                  'Not applicable to this session'
                ) : (
                  <NotesList items={professionalDevelopmentTools.map(({ name }) => name)} />
                )}
                {professionalDevelopmentToolsOther && (
                  <NotesOther other={professionalDevelopmentToolsOther} />
                )}
              </NotesItem>

              {additionalFeedback && (
                <NotesItem title={SessionNotesQuestions.additionalFeedback}>
                  {additionalFeedback}
                </NotesItem>
              )}
            </>
          )}
        </MoSpacing>
      </>
    );
  }

  return null;
}
